import React, { useEffect, useState } from 'react';
import { useLocalize } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { msgs } from '../../utilities/messages';

import { CODE_LENGTH } from './constants';
import { getMfaErrorMessage } from './helpers';

import './MfaModal.css';

/**
 * TOTP-only MFA prompt. Presentational: all challenge orchestration lives in the
 * useMfaVerification hook; pass its `modalProps` fields into this component.
 */
function MfaModal({ isOpen, isVerifying, errorCode, onSubmit, onClose }) {
  const localize = useLocalize();
  const [code, setCode] = useState('');

  // Reset the field each time a fresh challenge opens.
  useEffect(() => {
    if (isOpen) setCode('');
  }, [isOpen]);

  const isCodeValid = code.length === CODE_LENGTH;
  const canSubmit = isCodeValid && !isVerifying;

  const handleSetCode = (value) => {
    setCode(value.replace(/\D/g, '').slice(0, CODE_LENGTH));
  };

  const handleSubmit = () => {
    if (canSubmit) onSubmit(code);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && canSubmit) {
      event.preventDefault();
      onSubmit(code);
    }
  };

  const errorText = errorCode ? getMfaErrorMessage(localize, errorCode) : null;

  const contentBody = (
    <Box
      className='MfaModal-body'
      role='form'
      aria-label={localize(msgs.pam.mfa.title)}>
      <InputField
        name='mfaCode'
        label={
          <Typography kind='h4' htmlFor='mfaCode'>
            {localize(msgs.pam.mfa.codeLabel)}
          </Typography>
        }
        placeholder={localize(msgs.pam.mfa.codePlaceholder)}
        backgroundColor='secondary'
        value={code}
        setValue={handleSetCode}
        maxLength={CODE_LENGTH}
        inputMode='numeric'
        autoComplete='one-time-code'
        isDisabled={isVerifying}
        onKeyDown={handleKeyDown}
        isCritical={Boolean(errorText)}
        CriticalHelperText={
          errorText ? (
            <InlineMessage
              text={errorText}
              colorTheme='critical'
              size='small'
              leftIcon={<Icon kind='AlertCriticalFilled' />}
            />
          ) : null
        }
      />
    </Box>
  );

  const footerContent = (
    <FooterActionBar className='MfaModal-footer'>
      <Box className='MfaModal-footerButtons'>
        <Button type='button' kind='ghost' size='medium' onClick={onClose}>
          {localize(msgs.pam.common.cancel)}
        </Button>
        <Button
          type='button'
          kind={canSubmit ? 'filled' : 'outlined'}
          size='medium'
          isLoading={isVerifying}
          loaderText={localize(msgs.pam.mfa.verifying)}
          isDisabled={!canSubmit}
          onClick={handleSubmit}>
          {localize(msgs.pam.mfa.verify)}
        </Button>
      </Box>
    </FooterActionBar>
  );

  return (
    <ModalIsland
      className='MfaModal'
      isOpen={isOpen}
      isCloseButtonVisible={!isVerifying}
      onClose={onClose}
      title={localize(msgs.pam.mfa.title)}
      subtitle={localize(msgs.pam.mfa.subtitle)}
      contentBody={contentBody}
      FooterActionBarComp={footerContent}
    />
  );
}

MfaModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isVerifying: PropTypes.bool.isRequired,
  errorCode: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

MfaModal.defaultProps = {
  errorCode: null,
};

export default MfaModal;
